// lib/api.ts
import axios, { AxiosRequestConfig, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getCookie, deleteCookie } from 'cookies-next';
import { toast } from 'react-toastify';

// Utility function to extract path from URL
const extractPath = (url: string | undefined): string => {
  if (!url) return 'unknown';
  // Remove query parameters and hash, keep only the path
  const path = url.split('?')[0].split('#')[0];
  return path || 'unknown';
};

// Create Axios instance
console.log('API URL being used:', process.env.NEXT_PUBLIC_API_URL);
const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to dismiss toasts while preserving push notification toasts
const dismissToastsPreservingPushNotifications = () => {
  // Check if there are any active push notification toasts
  const pushNotificationToasts = (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) 
    ? (window as any).__pushNotificationToastIds as Set<string>
    : new Set<string>();
  
  // If there are active push notification toasts, skip dismissing toasts entirely
  // to prevent push notification toasts from being dismissed
  if (pushNotificationToasts.size > 0) {
    console.log('[API Interceptor] Skipping toast dismissal - push notification toasts are active');
    return;
  }
  
  // Only dismiss toasts if there are no push notification toasts
  toast.dismiss();
};

// Request interceptor for auth headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip dismissing toasts for push notification related API calls
    // to prevent push notification toasts from being dismissed immediately after display
    const isAuditApi = config.url?.includes('/v1/audit');
    const isPushNotificationApi = isAuditApi && 
                                  (typeof config.data === 'string' 
                                    ? config.data.includes('push_notification')
                                    : JSON.stringify(config.data || {}).includes('push_notification'));
    
    // Also skip for notification-related endpoints
    const isNotificationEndpoint = config.url?.includes('notification') || 
                                   config.url?.includes('firebase');
    
    if (!isPushNotificationApi && !isNotificationEndpoint) {
      // Only dismiss toasts for non-push-notification API calls
      dismissToastsPreservingPushNotifications();
    }

    const token = getCookie("token"); // Adjust based on your auth setup
    if (token && config.headers) {
      config.headers.Token = `${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    // Handle request setup errors (e.g., invalid config)
    return Promise.reject({
      message: 'Request configuration failed',
      details: error.message,
    });
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Return successful responses directly
    return response;
  },
  (error: AxiosError) => {
    // Extract response data and status
    const { response, request, config } = error;
    let errorMessage = 'An unexpected error occurred';
    const statusCode = response?.status;

    const token = getCookie("token");

    const requestUrl = config?.url || 'unknown';
    const baseUrl = config?.baseURL || 'unknown';
    const requestPath = extractPath(config?.url); // Capture request path

    // Create a custom error object
    const customError = {
      message: errorMessage,
      status: statusCode,
      details: response?.data || error.message,
    };

    const details = {
      message: "",
      error_message: ""
    }

    if (response) {
      // Server responded with a status code
      if (statusCode == 401) {
          // Call logout API before clearing cookies
          logoutUser().catch(error => {
            console.error('Error calling logout API in 401 handler:', error);
            // Continue with logout even if API call fails
          });

          // Optional: Trigger logout or token refresh
          document.cookie = 'isAuthenticated=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          deleteCookie("token")
          deleteCookie("ca_user_id")
          deleteCookie("company_id")
          deleteCookie("company_user_id")
          deleteCookie("user_type")
          deleteCookie("user_type_id")
          deleteCookie("user_pay_type")
          deleteCookie("user_role")
          deleteCookie("user_role_id")
          deleteCookie("user_full_name")
          deleteCookie("first_name")
          deleteCookie("middle_name")
          deleteCookie("last_name")
          deleteCookie("password_expiry_dtm")
          deleteCookie("locked_out_flag")
          deleteCookie("email")
          deleteCookie("ca_user_active_flag")
          deleteCookie("company_user_active_flag")
          deleteCookie("user_company_association_flag")
          deleteCookie("sns_id")
          deleteCookie("message")
          deleteCookie("status")
          deleteCookie("session_timeout_minutes")

          window.location.href = '/login';
      }

      if (statusCode == 400 || statusCode == 404 || statusCode == 500) {
        if(requestPath != "/v1/care_ascendium_error/new") {
          console.log("Error Block: " + response.data);

          api.post("/v1/care_ascendium_error/new", {
            p_request_path: requestPath,
            p_ca_app: "Web",
            p_error_desc: (response.data as any)?.message?.replace(/'/g, '"') || (response.data as any)?.error?.replace(/'/g, '"') || 'Unknown error',
            p_ca_user_id: getCookie("ca_user_id"),
            p_ca_user_email: getCookie("email"),
            p_device_info: navigator.userAgent
          }).then((response: any) => {
            console.log(response.data);

            toast.error("We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.", 
              {
                toastId: 'error1',
                autoClose: 5000
              } 
            );
          });
        }
      }
    } else if (request) {
      // Request was made but no response received (e.g., network error)
      errorMessage = 'No internet connection. Please check your network and try again.';

      console.log(customError);

      details.error_message = errorMessage;
      details.message = errorMessage;

      customError.details = details;
    } else {
      // Error setting up the request
      errorMessage = `Request Error: ${error.message}`;
    }

    customError.message = errorMessage;

    // Log error for debugging (optional)
    console.log('API Error:', customError);

    // Reject with custom error
    return Promise.reject(customError);
  }
);

export default api;

// Audit API functions
import { getCombinedInfo } from '../utils/browserUtils';

export interface AuditData {
  company_id?: string | number;
  ca_user_id?: string | number;
  company_user_id?: string | number;
  authentication_token?: string;
  ui_app?: string;
  device_or_browser?: string;
  os?: string;
  version?: string;
  page?: string;
  page_status?: string;
  sp?: string;
  action?: string;
  audit_message?: string;
  logged_in_company_user_id?: string | number;
}

export async function createAudit(auditData: AuditData) {
  try {
    // Get browser and app information
    const { browser, app } = getCombinedInfo();
    
    // Prepare the request payload with the specific fields
    const payload = {
      p_company_id: auditData.company_id || getCookie("company_id"),
      p_ca_user_id: auditData.ca_user_id || getCookie("ca_user_id"),
      p_company_user_id: auditData.company_user_id || getCookie("company_user_id"),
      p_authentication_token: auditData.authentication_token || getCookie("token"),
      p_ui_app: "Web App",
      p_device_or_browser: auditData.device_or_browser || browser.browser,
      p_os: auditData.os || browser.os,
      p_version: auditData.version || browser.browserVersion,
      p_page: auditData.page || app.currentUrl,
      p_page_status: auditData.page_status || "active",
      p_sp: auditData.sp || "Not Provided",
      p_action: auditData.action || "audit_log",
      p_audit_message: auditData.audit_message || "Audit event logged",
      p_logged_in_company_user_id: auditData.logged_in_company_user_id || getCookie("company_user_id")
    };

    const response = await api.post('/v1/ca_audit/new', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating audit:', error);
    throw error;
  }
}

// Convenience function for simple audit logging
export async function logAuditEvent(
  action: string,
  auditMessage: string,
  page?: string,
  additionalData?: Partial<AuditData>
) {
  return createAudit({
    action,
    audit_message: auditMessage,
    page,
    ...additionalData
  });
}

// Logout API function
export async function logoutUser() {
  try {
    const token = getCookie("token");
    const caUserId = getCookie("ca_user_id");
    const companyId = getCookie("company_id");
    const companyUserId = getCookie("company_user_id");

    if (token && caUserId && companyId && companyUserId) {
      await api.post('/v1/user_authentication/user_logout', {
        p_ca_user_id: caUserId,
        p_company_id: companyId,
        p_company_user_id: companyUserId,
        p_authentication_token: token
      });
    }
  } catch (error) {
    console.error('Error calling logout API:', error);
    // Don't throw error - logout should proceed even if API call fails
  }
}
